/**
 * Numérotation des factures et communication structurée belge.
 *
 * Le format n'est plus figé : il vient des paramètres de facturation servis par
 * l'API (clé `numerotation`), parce que le préfixe ne restera pas « FACT ». Le
 * module garde ce format en mémoire, posé une fois au démarrage par
 * definirFormatNumerotation() ; tout le reste demeure pur, le compteur compris.
 *
 * Attention au lien entre numéro et communication structurée : celle-ci se
 * construit sur l'année et le RANG, pas sur le préfixe. Il faut donc que
 * decomposerNumero() reconnaisse le format en vigueur, sans quoi elle retombe
 * sur « les chiffres du numéro » et produit une autre communication pour le
 * même rang.
 */

const FORMAT_DEFAUT = {
  gabarit: '{PREFIXE}-{ANNEE}-{RANG}',
  prefixe: 'FACT',
  rangChiffres: 4
}

let formatCourant = Object.assign({}, FORMAT_DEFAUT)

function completerAGauche (valeur, longueur) {
  let texte = String(valeur)
  while (texte.length < longueur) {
    texte = '0' + texte
  }
  return texte
}

/** Le préfixe vient d'un formulaire : neutraliser ses métacaractères. */
function echapper (texte) {
  return String(texte).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Pose le format servi par l'API. `null` revient au format historique, ce qui
 * est aussi l'état tant que les paramètres n'ont pas été lus : les deux dépôts
 * peuvent ainsi être déployés séparément.
 */
export function definirFormatNumerotation (numerotation) {
  const recu = numerotation || {}
  const gabarit = typeof recu.gabarit === 'string' &&
    recu.gabarit.indexOf('{ANNEE}') !== -1 && recu.gabarit.indexOf('{RANG}') !== -1
    ? recu.gabarit
    : FORMAT_DEFAUT.gabarit
  const rang = parseInt(recu.rangChiffres, 10)

  formatCourant = {
    gabarit: gabarit,
    prefixe: typeof recu.prefixe === 'string' ? recu.prefixe : FORMAT_DEFAUT.prefixe,
    rangChiffres: rang >= 1 && rang <= 8 ? rang : FORMAT_DEFAUT.rangChiffres
  }
  return formatCourant
}

const TROUS = ['{PREFIXE}', '{ANNEE}', '{RANG}']
const ALPHABET = /^[A-Za-z0-9_-]*$/
const RANG_CHIFFRES_MAX = 8
/** abft_factures.numero est un string(32) unique. */
const LONGUEUR_MAX = 32

function compterOccurrences (texte, motif) {
  return String(texte).split(motif).length - 1
}

function gabaritValide (gabarit) {
  if (typeof gabarit !== 'string' ||
      gabarit.indexOf('{ANNEE}') === -1 || gabarit.indexOf('{RANG}') === -1) {
    return false
  }
  // Un trou répété n'a pas le même sens des deux côtés : le serveur le refuse.
  for (let i = 0; i < TROUS.length; i++) {
    if (compterOccurrences(gabarit, TROUS[i]) > 1) return false
  }
  // Les littéraux, soit le gabarit privé de ses trous : un {MOIS} inconnu y
  // resterait et serait rejeté par l'alphabet, comme un « / » ou un « .. ».
  let litteraux = gabarit
  TROUS.forEach(function (trou) { litteraux = remplacerTout(litteraux, trou, '') })
  return ALPHABET.test(litteraux)
}

function prefixeValide (prefixe) {
  return typeof prefixe === 'string' && ALPHABET.test(prefixe)
}

function rangValide (rang) {
  const entier = typeof rang === 'number' ? rang
    : (typeof rang === 'string' && /^\d+$/.test(rang) ? parseInt(rang, 10) : NaN)
  return entier >= 1 && entier <= RANG_CHIFFRES_MAX
}

/**
 * Les motifs de refus d'un format, en clair. Objet vide = format acceptable.
 *
 * Reprend les règles de App\Outils\Facture\Numerotation pour éviter un
 * aller-retour : le serveur reste juge, mais autant ne pas lui soumettre ce
 * qu'on sait déjà fautif. Les clés absentes sont celles d'aujourd'hui, pour
 * qu'une mise à jour partielle ne soit pas refusée sur ce qu'elle ne touche pas.
 */
export function erreursDeFormat (brut) {
  const propose = Object.assign({}, FORMAT_DEFAUT, brut || {})
  const erreurs = {}

  if (!gabaritValide(propose.gabarit)) {
    erreurs.gabarit = 'Le gabarit doit contenir {ANNEE} et {RANG}, n’employer que les trous ' +
      TROUS.join(', ') + ' — chacun au plus une fois — et ne comporter autour que des lettres, ' +
      'chiffres, tirets ou soulignements (le numéro sert de nom de fichier).'
  }
  if (!prefixeValide(propose.prefixe)) {
    erreurs.prefixe = 'Le préfixe ne peut contenir que des lettres, des chiffres, des tirets ' +
      'ou des soulignements. Il peut être vide.'
  }
  if (!rangValide(propose.rangChiffres)) {
    erreurs.rangChiffres = 'Le nombre de chiffres du rang doit être un entier de 1 à ' +
      RANG_CHIFFRES_MAX + '.'
  }

  if (!Object.keys(erreurs).length) {
    // Le rang peut s'allonger jusqu'au maximum : c'est lui qui doit tenir.
    const plusLong = apercuFormat(propose).plusLong
    if (plusLong.length > LONGUEUR_MAX) {
      erreurs.longueur = 'Le numéro le plus long que ce format produirait (' + plusLong + ', ' +
        plusLong.length + ' caractères) dépasse les ' + LONGUEUR_MAX + ' caractères de la colonne.'
    }
  }

  return erreurs
}

/**
 * Ce qu'un format donnerait, sans toucher à celui en vigueur : l'écran
 * d'administration prévisualise avant d'enregistrer.
 */
export function apercuFormat (brut, annee) {
  const precedent = formatCourant
  const exercice = annee || new Date().getFullYear()
  try {
    definirFormatNumerotation(brut)
    const plusLong = appliquerGabarit(exercice, Array(RANG_CHIFFRES_MAX + 1).join('9'))
    const exemple = formaterNumero(exercice, 1)
    return {
      exemple: exemple,
      plusLong: plusLong,
      longueur: plusLong.length,
      // Calculée ICI, sous le format prévisualisé : au retour, le format en
      // vigueur est restauré et le numéro ne serait plus reconnu.
      communication: communicationStructuree(exemple)
    }
  } finally {
    formatCourant = precedent
  }
}

export function formatNumerotation () {
  return Object.assign({}, formatCourant)
}

/** Remplace TOUTES les occurrences, comme le str_replace du serveur. */
function remplacerTout (texte, trou, valeur) {
  return String(texte).split(trou).join(valeur)
}

function appliquerGabarit (annee, rang) {
  let resultat = remplacerTout(formatCourant.gabarit, '{PREFIXE}', formatCourant.prefixe)
  resultat = remplacerTout(resultat, '{ANNEE}', String(annee))
  return remplacerTout(resultat, '{RANG}', String(rang))
}

/**
 * Le gabarit sert aussi à produire l'expression qui reconnaît un numéro.
 *
 * Tout est échappé d'abord, puis les trous — eux-mêmes échappés — sont
 * remplacés : un littéral contenant « . » ou « ( » ouvrirait sinon la
 * validation. Le serveur filtre déjà les littéraux, mais le client ne doit pas
 * en dépendre pour ne pas composer une expression folle.
 */
function expressionNumero (chiffresRang) {
  let motif = echapper(formatCourant.gabarit)
  motif = remplacerTout(motif, echapper('{PREFIXE}'), echapper(formatCourant.prefixe))
  motif = remplacerTout(motif, echapper('{ANNEE}'), '(\\d{4})')
  motif = remplacerTout(motif, echapper('{RANG}'), chiffresRang)
  return new RegExp('^' + motif + '$')
}

/**
 * Quel groupe de capture porte l'année, et lequel le rang.
 *
 * Les groupes sont positionnels : leur rang dépend donc de l'ordre des trous
 * dans le gabarit, que rien n'impose. Supposer « année puis rang » décomposait
 * à l'envers un gabarit comme {RANG}-{ANNEE}, et la communication structurée,
 * bâtie sur l'année et le rang, en sortait fausse. Le serveur règle le même
 * problème avec des groupes nommés, hors de portée des navigateurs visés ici.
 */
function ordreDesGroupes () {
  return formatCourant.gabarit.indexOf('{ANNEE}') < formatCourant.gabarit.indexOf('{RANG}')
    ? {annee: 1, rang: 2}
    : {annee: 2, rang: 1}
}

export function formaterNumero (annee, sequence) {
  return appliquerGabarit(annee, completerAGauche(sequence, formatCourant.rangChiffres))
}

export function decomposerNumero (numero) {
  const correspondance = expressionNumero('(\\d+)').exec(String(numero || ''))
  if (!correspondance) return null
  const ordre = ordreDesGroupes()
  return {
    annee: parseInt(correspondance[ordre.annee], 10),
    sequence: parseInt(correspondance[ordre.rang], 10)
  }
}

/**
 * Renvoie le prochain numéro et le compteur mis à jour, sans muter l'entrée :
 * l'appelant décide s'il persiste le nouveau compteur (on ne consomme un numéro
 * que lorsque la facture est réellement enregistrée).
 */
export function prochainNumero (compteurs, annee) {
  const copie = Object.assign({}, compteurs || {})
  const cle = String(annee)
  const sequence = (parseInt(copie[cle], 10) || 0) + 1
  copie[cle] = sequence
  return {
    numero: formaterNumero(annee, sequence),
    compteurs: copie
  }
}

/**
 * Clé de contrôle d'une communication structurée : reste de la division par 97,
 * et 97 lorsque le reste est nul (une clé « 00 » n'existe pas).
 */
export function cleModulo97 (base) {
  const nombre = parseInt(String(base).replace(/\D/g, ''), 10) || 0
  const reste = nombre % 97
  return reste === 0 ? 97 : reste
}

/**
 * Base de 10 chiffres : 2 chiffres d'année + séquence sur 8 chiffres. Le
 * préfixe n'y entre pas, donc en changer ne change aucune communication déjà
 * émise. Pour un numéro hors gabarit, on retombe sur les chiffres qu'il
 * contient.
 */
function baseDepuisNumero (numero) {
  const decompose = decomposerNumero(numero)
  if (decompose) {
    const anneeCourte = completerAGauche(decompose.annee % 100, 2)
    return anneeCourte + completerAGauche(decompose.sequence, 8).slice(-8)
  }
  const chiffres = String(numero || '').replace(/\D/g, '')
  return completerAGauche(chiffres, 10).slice(-10)
}

export function communicationStructuree (numero) {
  const base = baseDepuisNumero(numero)
  const douzeChiffres = base + completerAGauche(cleModulo97(base), 2)
  return '+++' + douzeChiffres.slice(0, 3) + '/' + douzeChiffres.slice(3, 7) +
    '/' + douzeChiffres.slice(7, 12) + '+++'
}

export function communicationStructureeValide (communication) {
  const chiffres = String(communication || '').replace(/\D/g, '')
  if (chiffres.length !== 12) return false
  const attendue = completerAGauche(cleModulo97(chiffres.slice(0, 10)), 2)
  return chiffres.slice(10, 12) === attendue
}

/**
 * Détection de doublon. `idEnCours` exclut la facture en cours d'édition :
 * rouvrir une facture de l'historique ne doit pas la signaler comme doublon
 * d'elle-même.
 */
export function numeroDejaUtilise (numero, historique, idEnCours) {
  const recherche = String(numero || '').trim().toLowerCase()
  if (!recherche) return false
  return (historique || []).some(function (facture) {
    if (idEnCours && facture.id === idEnCours) return false
    return String(facture.numero || '').trim().toLowerCase() === recherche
  })
}

/**
 * Le serveur n'accepte qu'un numéro conforme au gabarit en vigueur, avec un
 * rang d'au moins `rangChiffres` chiffres et d'au plus 8. Hors format, il le
 * remplace par le suivant de sa propre série, sans prévenir : autant le dire
 * à la saisie.
 */
export function numeroAcceptableParServeur (numero) {
  const mini = formatCourant.rangChiffres
  return expressionNumero('\\d{' + mini + ',8}').test(String(numero || ''))
}

/** Le format attendu, en clair, pour les messages d'erreur. */
export function formatLisible () {
  return appliquerGabarit('AAAA', completerAGauche('', formatCourant.rangChiffres)
    .replace(/0/g, 'N'))
}

/**
 * Rend utilisable ce que le secrétariat tape : « 42 » devient
 * FACT-2026-0042, un numéro déjà complet garde SON année (on renumérote
 * parfois un lot sur l'exercice précédent). null si rien d'exploitable.
 */
export function normaliserNumeroSaisi (saisie, annee) {
  const texte = String(saisie === null || saisie === undefined ? '' : saisie).trim().toUpperCase()
  if (!texte) return null

  const complet = decomposerNumero(texte)
  if (complet) {
    return complet.sequence > 0 ? formaterNumero(complet.annee, complet.sequence) : null
  }

  if (!/^\d+$/.test(texte)) return null
  const rang = parseInt(texte, 10)
  return rang > 0 ? formaterNumero(annee, rang) : null
}

/** Numéros consécutifs depuis un départ, pour renuméroter un lot entier. */
export function sequenceNumeros (depart, nombre) {
  const decompose = decomposerNumero(depart)
  const total = parseInt(nombre, 10) || 0
  if (!decompose || total <= 0) return []

  const numeros = []
  for (let i = 0; i < total; i++) {
    numeros.push(formaterNumero(decompose.annee, decompose.sequence + i))
  }
  return numeros
}

/**
 * Numéros présents plus d'une fois dans un lot. Le serveur arbitrerait de
 * toute façon, mais mieux vaut le signaler avant d'enregistrer.
 */
export function doublonsDansLot (numeros) {
  const vus = {}
  const repetes = []
  ;(numeros || []).forEach(function (numero) {
    const cle = String(numero || '').trim().toLowerCase()
    if (!cle) return
    if (vus[cle] === 1) repetes.push(String(numero).trim())
    vus[cle] = (vus[cle] || 0) + 1
  })
  return repetes
}

export default {
  formaterNumero,
  decomposerNumero,
  prochainNumero,
  cleModulo97,
  communicationStructuree,
  communicationStructureeValide,
  numeroDejaUtilise,
  numeroAcceptableParServeur,
  formatLisible,
  definirFormatNumerotation,
  formatNumerotation,
  erreursDeFormat,
  apercuFormat,
  normaliserNumeroSaisi,
  sequenceNumeros,
  doublonsDansLot
}



// WEBPACK FOOTER //
// ./src/utils/facture/numerotation.js